/* eslint-disable quote-props */
import {HeroIngredient, InstantCheckupAiResponseResultItem} from '@cureskin/api-client/src/server-api';
import { ParameterViewModel } from '../../helper/report-helper/report-helper';
export { PRIVATE_MAIN_CONCERN_DISPLAY_NAME } from '../user-regimen/regimen-products-detail/doctor-notes-constants';

export interface AiScalpParameter {
  'name'?: string | AiLocalizedString;
  'value_percent'?: number;
  'flag'?: string;
  'target_range'?: string;
  'confidence'?: string;
  'not_scored'?: boolean;
  'reason_if_not_scored'?: string | AiLocalizedString | null;
}

export interface AiLocalizedString {
  'en'?: string | null;
  'hi'?: string | null;
}

export interface AiLocalizedSummary {
  'paragraph_1'?: string;
  'paragraph_2'?: string;
}

export interface AiHairReport {
  'main_concern'?: string | AiLocalizedString;
  'other_concerns'?: Array<string | AiLocalizedString>;
  'overall_summary'?: AiLocalizedSummary | {
    'en'?: AiLocalizedSummary,
    'hi'?: AiLocalizedSummary,
  };
  'key_scalp_parameters'?: AiScalpParameter[];
  'overall_score'?: number;
  'overall_score_logic'?: string | AiLocalizedString;
}

export interface ReportDetails {
  summaryParagraphs: string[];
  primaryConcern: string;
  secondaryConcerns: string;
  severity: string;
  parameters: ParameterViewModel[];
}

export interface ConcernsWithCount {
  infAcne: number;
  comedone: number;
  openPores: number;
  ds: number;
  dc: number;
  melasma: number;
  acneScars: number;
  sensitiveSkin: number;
}

export interface SkinParameters {
  patientScore: number;
  expectedRange: string;
}

export interface RootCauses {
  [key: string]: string;
}

export interface SkinData {
  sebumBalanceParameters: SkinParameters;
  inflammationParameters: SkinParameters;
  sensitivityParameters: SkinParameters;
  skinDamageParameters: SkinParameters;
  skinRenewalParameters: SkinParameters;
  skinRadianceParameters: SkinParameters;
}

export interface HairData {
  hairDensityParameters: SkinParameters;
  hairStrengthParameters: SkinParameters;
  scalpOilBalanceParameters: SkinParameters;
  hairSmoothnessParameters: SkinParameters;
  prematureGreyingParameters: SkinParameters;
  greyingParameters: SkinParameters;
}

export interface DetectionResult extends InstantCheckupAiResponseResultItem {
  Severity?: string;
}

export interface SkinReportInfo {
  skinParameters?: SkinData;
  overallSummary?: { overallSummary?: string };
}

export type HairFallbackEntry = Partial<Record<keyof HairData, SkinParameters | null>>;

export const HAIR_REPORT_FALLBACK_PARAMS: Readonly<Record<string, Record<string, HairFallbackEntry>>> = {
  DANDRUFF: {
    Moderate: {
      hairDensityParameters: null,
      hairStrengthParameters: { patientScore: 6, expectedRange: '8-10' },
      scalpOilBalanceParameters: { patientScore: 8, expectedRange: '4-6' },
      hairSmoothnessParameters: { patientScore: 5, expectedRange: '8-10' },
    },
  },
  DANDRUFF_AND_HAIR_LOSS: {
    Moderate: {
      hairDensityParameters: { patientScore: 4, expectedRange: '8-10' },
      hairStrengthParameters: { patientScore: 5, expectedRange: '8-10' },
      scalpOilBalanceParameters: { patientScore: 8, expectedRange: '4-6' },
      hairSmoothnessParameters: null,
    },
  },
  FRIZZY_HAIR: {
    Moderate: {
      hairDensityParameters: null,
      hairStrengthParameters: { patientScore: 4, expectedRange: '8-10' },
      scalpOilBalanceParameters: { patientScore: 4, expectedRange: '4-6' },
      hairSmoothnessParameters: { patientScore: 3, expectedRange: '8-10' },
    },
  },
  FRIZZY_HAIR_AND_DANDRUFF: {
    Moderate: {
      hairDensityParameters: null,
      hairStrengthParameters: { patientScore: 3, expectedRange: '8-10' },
      scalpOilBalanceParameters: { patientScore: 8, expectedRange: '4-6' },
      hairSmoothnessParameters: { patientScore: 2, expectedRange: '8-10' },
    },
  },
  FRIZZY_HAIR_AND_HAIR_LOSS: {
    Moderate: {
      hairDensityParameters: { patientScore: 5, expectedRange: '8-10' },
      hairStrengthParameters: { patientScore: 4, expectedRange: '8-10' },
      scalpOilBalanceParameters: null,
      hairSmoothnessParameters: { patientScore: 3, expectedRange: '8-10' },
    },
  },
  HAIR_LOSS: {
    Mild: {
      hairDensityParameters: { patientScore: 6, expectedRange: '8-10' },
      hairStrengthParameters: { patientScore: 7, expectedRange: '8-10' },
      scalpOilBalanceParameters: { patientScore: 5, expectedRange: '4-6' },
      hairSmoothnessParameters: null,
    },
    Moderate: {
      hairDensityParameters: { patientScore: 4, expectedRange: '8-10' },
      hairStrengthParameters: { patientScore: 5, expectedRange: '8-10' },
      scalpOilBalanceParameters: { patientScore: 6, expectedRange: '4-6' },
      hairSmoothnessParameters: null,
    },
    Severe: {
      hairDensityParameters: { patientScore: 2, expectedRange: '8-10' },
      hairStrengthParameters: { patientScore: 4, expectedRange: '8-10' },
      scalpOilBalanceParameters: { patientScore: 5, expectedRange: '4-6' },
      hairSmoothnessParameters: null,
    },
  },
  REGULAR_HAIR_CARE: {
    Moderate: {
      hairDensityParameters: { patientScore: 7, expectedRange: '8-10' },
      hairStrengthParameters: { patientScore: 9, expectedRange: '8-10' },
      scalpOilBalanceParameters: { patientScore: 6, expectedRange: '4-6' },
      hairSmoothnessParameters: null,
    },
  },
  GREY_HAIR: {
    Moderate: {
      hairDensityParameters: { patientScore: 6, expectedRange: '8-10' },
      hairStrengthParameters: { patientScore: 8, expectedRange: '8-10' },
      scalpOilBalanceParameters: { patientScore: 5, expectedRange: '4-6' },
      hairSmoothnessParameters: null,
    },
  },
};

export type SkinFallbackEntry = Partial<Record<keyof SkinData, SkinParameters | null>>;

const sebumIdeal = '4-6';
const inflammationIdeal = '0-2';
const skinDamageIdeal = '0-2';
const skinRenewalIdeal = '5-7';

export const SKIN_REPORT_FALLBACK_PARAMS: Readonly<Record<string, Record<string, SkinFallbackEntry>>> = {
  ACNE_OR_PIMPLES: {
    Mild: {
      sebumBalanceParameters: { patientScore: 7, expectedRange: sebumIdeal },
      inflammationParameters: { patientScore: 3, expectedRange: inflammationIdeal },
      skinDamageParameters: { patientScore: 4, expectedRange: skinDamageIdeal },
      skinRenewalParameters: { patientScore: 3, expectedRange: skinRenewalIdeal },
    },
    default: {
      sebumBalanceParameters: { patientScore: 8, expectedRange: sebumIdeal },
      inflammationParameters: { patientScore: 4, expectedRange: inflammationIdeal },
      skinDamageParameters: { patientScore: 5, expectedRange: skinDamageIdeal },
      skinRenewalParameters: { patientScore: 2, expectedRange: skinRenewalIdeal },
    },
  },
  ACNE_SCARS: {
    default: {
      sebumBalanceParameters: { patientScore: 7, expectedRange: sebumIdeal },
      inflammationParameters: { patientScore: 3, expectedRange: inflammationIdeal },
      skinDamageParameters: { patientScore: 4, expectedRange: skinDamageIdeal },
      skinRenewalParameters: { patientScore: 3, expectedRange: skinRenewalIdeal },
    },
  },
  ANTI_AGING: {
    default: {
      sebumBalanceParameters: { patientScore: 4, expectedRange: sebumIdeal },
      inflammationParameters: { patientScore: 1, expectedRange: inflammationIdeal },
      skinDamageParameters: { patientScore: 5, expectedRange: skinDamageIdeal },
      skinRenewalParameters: { patientScore: 1, expectedRange: skinRenewalIdeal },
    },
  },
  COMEDONES: {
    DARK_SPOTS_OR_MARK: {
      sebumBalanceParameters: { patientScore: 8, expectedRange: sebumIdeal },
      inflammationParameters: { patientScore: 5, expectedRange: inflammationIdeal },
      skinDamageParameters: { patientScore: 2, expectedRange: skinDamageIdeal },
      skinRenewalParameters: { patientScore: 4, expectedRange: skinRenewalIdeal },
    },
    Severe: {
      sebumBalanceParameters: { patientScore: 9, expectedRange: sebumIdeal },
      inflammationParameters: { patientScore: 5, expectedRange: inflammationIdeal },
      skinDamageParameters: { patientScore: 4, expectedRange: skinDamageIdeal },
      skinRenewalParameters: { patientScore: 1, expectedRange: skinRenewalIdeal },
    },
    default: {
      sebumBalanceParameters: { patientScore: 8, expectedRange: sebumIdeal },
      inflammationParameters: { patientScore: 5, expectedRange: inflammationIdeal },
      skinDamageParameters: { patientScore: 4, expectedRange: skinDamageIdeal },
      skinRenewalParameters: { patientScore: 3, expectedRange: skinRenewalIdeal },
    },
  },
  DARK_SPOTS_OR_MARK: {
    default: {
      sebumBalanceParameters: { patientScore: 5, expectedRange: sebumIdeal },
      inflammationParameters: { patientScore: 3, expectedRange: inflammationIdeal },
      skinDamageParameters: { patientScore: 5, expectedRange: skinDamageIdeal },
      skinRenewalParameters: { patientScore: 3, expectedRange: skinRenewalIdeal },
    },
  },
  DULL_SKIN: {
    default: {
      sebumBalanceParameters: { patientScore: 4, expectedRange: sebumIdeal },
      inflammationParameters: { patientScore: 1, expectedRange: inflammationIdeal },
      skinDamageParameters: { patientScore: 5, expectedRange: skinDamageIdeal },
      skinRenewalParameters: { patientScore: 3, expectedRange: skinRenewalIdeal },
    },
  },
  PIGMENTATION: {
    COMEDONES: {
      sebumBalanceParameters: { patientScore: 7, expectedRange: sebumIdeal },
      inflammationParameters: { patientScore: 3, expectedRange: inflammationIdeal },
      skinDamageParameters: { patientScore: 4, expectedRange: skinDamageIdeal },
      skinRenewalParameters: { patientScore: 3, expectedRange: skinRenewalIdeal },
    },
    Mild: {
      sebumBalanceParameters: { patientScore: 6, expectedRange: sebumIdeal },
      inflammationParameters: { patientScore: 1, expectedRange: inflammationIdeal },
      skinDamageParameters: { patientScore: 4, expectedRange: skinDamageIdeal },
      skinRenewalParameters: { patientScore: 3, expectedRange: skinRenewalIdeal },
    },
    default: {
      sebumBalanceParameters: { patientScore: 5, expectedRange: sebumIdeal },
      inflammationParameters: { patientScore: 1, expectedRange: inflammationIdeal },
      skinDamageParameters: { patientScore: 6, expectedRange: skinDamageIdeal },
      skinRenewalParameters: { patientScore: 2, expectedRange: skinRenewalIdeal },
    },
  },
  UNDER_EYE_DARK_CIRCLES: {
    ANTI_AGING: {
      sebumBalanceParameters: { patientScore: 5, expectedRange: sebumIdeal },
      inflammationParameters: { patientScore: 3, expectedRange: inflammationIdeal },
      skinDamageParameters: { patientScore: 5, expectedRange: skinDamageIdeal },
      skinRenewalParameters: { patientScore: 1, expectedRange: skinRenewalIdeal },
    },
    COMEDONES: {
      sebumBalanceParameters: { patientScore: 8, expectedRange: sebumIdeal },
      inflammationParameters: { patientScore: 5, expectedRange: inflammationIdeal },
      skinDamageParameters: { patientScore: 5, expectedRange: skinDamageIdeal },
      skinRenewalParameters: { patientScore: 3, expectedRange: skinRenewalIdeal },
    },
    default: {
      sebumBalanceParameters: { patientScore: 5, expectedRange: sebumIdeal },
      inflammationParameters: { patientScore: 1, expectedRange: inflammationIdeal },
      skinDamageParameters: { patientScore: 6, expectedRange: skinDamageIdeal },
      skinRenewalParameters: { patientScore: 2, expectedRange: skinRenewalIdeal },
    },
  },
};

export interface ConcernCountResult {
  payload: ConcernsWithCount;
  mainConcernKey: string;
  mainConcern: string;
}

export interface HeroIngredientsResult {
  ingredients: HeroIngredient[];
  show: boolean;
}

export const HAIR_CONCERNS = ['hairline', 'temple-baldness', 'scalp-segment'];

export const CONCERN_NAMES: Record<string, string> = {
  infAcne: 'Acne',
  comedone: 'Comedones',
  openPores: 'Open pores',
  ds: 'Dark spots',
  dc: 'Dark circles',
  melasma: 'Melasma',
  acneScars: 'Acne Scars',
  sensitiveSkin: 'Sensitive Skin',
};

export const PROBLEM_TO_CONCERN: Record<string, { key: string, useBoundingBoxCount: boolean }> = {
  'Inflammatory Acne': { key: 'infAcne', useBoundingBoxCount: true },
  'Comedones': { key: 'comedone', useBoundingBoxCount: true },
  'Acne Scars': { key: 'acneScars', useBoundingBoxCount: true },
  'Dark Spots': { key: 'ds', useBoundingBoxCount: true },
  'Open Pores': { key: 'openPores', useBoundingBoxCount: false },
  'Melasma': { key: 'melasma', useBoundingBoxCount: false },
  'Dark Circles': { key: 'dc', useBoundingBoxCount: false },
};

export const FIRST_JOURNEY_CONCERNS = ['Pigmentation', 'Acne Scars', 'Dark spots'];


export const DefaultDrVideo = ['https://cdn.cureskin.com/app/video/drCharu_Eng_1_skin.mp4'];
export const DefaultDrImages = [
  'https://cdn.cureskin.com/app/img/timer-page-inline-en-1.png',
  'https://cdn.cureskin.com/app/img/timer-page-inline-en-2.png',
];
